import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UI_LANGS } from "@/i18n";
import api, { apiError } from "@/lib/api";
import { ROLE_LABELS, STAFF_SUBROLES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users as UsersIcon, Loader2, Trash2, Link2, KeyRound, Copy, Check, Pencil, UserPlus, MessageCircle, Mail, RefreshCw, Clock, Search, Smartphone, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["client", "guest", "host", "cleaner", "subcontractor", "admin"];
const HOUSE_ROLES = ["client", "host", "guest"];
const chatDefaults = (role) => ({
  chat_house: ["admin", "host", "client"].includes(role),
  chat_guest: ["admin", "host", "client"].includes(role),
  chat_direct: ["admin", "host", "client", "cleaner", "subcontractor"].includes(role),
});
const emptyInvite = { name: "", email: "", phone: "", role: "client", house_ids: [], hourly_rate: "", language: "nl", ...chatDefaults("client") };

function LanguageSelect({ value, onChange, testId }) {
  return (
    <div>
      <Label>Taal van het portaal</Label>
      <Select value={value || "nl"} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{UI_LANGS.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function ChatPermToggles({ form, setForm, role }) {
  const isField = ["cleaner", "subcontractor"].includes(role);
  const Row = ({ k, title, desc, disabled }) => (
    <label className={`flex items-start gap-3 rounded-lg border border-border p-3 ${disabled ? "opacity-50" : "cursor-pointer"}`} data-testid={`chat-perm-${k}`}>
      <Checkbox checked={!!form[k]} disabled={disabled} onCheckedChange={(v) => setForm((f) => ({ ...f, [k]: !!v }))} className="mt-0.5" data-testid={`chat-perm-${k}-checkbox`} />
      <span className="text-sm">
        <span className="font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{desc}</span>
      </span>
    </label>
  );
  return (
    <div className="space-y-2">
      <Label>Welke chats mag deze gebruiker zien?</Label>
      <Row k="chat_house" title="Woningchats" desc="Algemene gesprekken per (gekoppelde) woning." disabled={isField} />
      <Row k="chat_guest" title="Gastchats" desc="Berichten van gasten van de (gekoppelde) woningen." disabled={isField} />
      <Row k="chat_direct" title="1-op-1 gesprekken" desc={isField ? "Alleen met admins en de toegewezen host(s)." : "Persoonlijke gesprekken met teamleden."} />
    </div>
  );
}

const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "");

const DeviceStatus = ({ u }) => (
  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`device-status-${u.id}`}>
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${u.app_installed_at ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}
      title={u.app_installed_at ? `Geïnstalleerd sinds ${fmtDay(u.app_installed_at)} · laatst geopend ${fmtDay(u.app_last_opened_at)}` : "App nog niet vanaf het startscherm geopend"}
      data-testid={`app-status-${u.id}`}>
      <Smartphone className="h-3 w-3" /> {u.app_installed_at ? "App geïnstalleerd" : "Geen app"}
    </span>
    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ${u.push_devices ? "bg-emerald-100 text-emerald-800" : "bg-muted text-muted-foreground"}`}
      data-testid={`push-status-${u.id}`}>
      {u.push_devices ? <Bell className="h-3 w-3" /> : <BellOff className="h-3 w-3" />}
      {u.push_devices ? `Meldingen aan${u.push_devices > 1 ? ` (${u.push_devices} apparaten)` : ""}` : "Meldingen uit"}
    </span>
  </div>
);

const DeviceSummary = ({ users }) => {
  const list = (users || []).filter((u) => u.role !== "guest");
  if (!list.length) return null;
  const app = list.filter((u) => u.app_installed_at).length;
  const push = list.filter((u) => u.push_devices).length;
  return (
    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4" data-testid="device-summary">
      <span className="inline-flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> App geïnstalleerd: <b className="text-foreground">{app}/{list.length}</b></span>
      <span className="inline-flex items-center gap-1.5"><Bell className="h-4 w-4" /> Meldingen aan: <b className="text-foreground">{push}/{list.length}</b></span>
    </div>
  );
};

const KeyAlsoUsers = ({ users, value, onChange }) => (
  <div className="rounded-lg border border-border p-3" data-testid="key-also-users">
    <div className="text-sm font-medium">Sleutelbeheer: ziet ook woningen van</div>
    <div className="text-xs text-muted-foreground mb-2">Naast de eigen woningen ziet deze gebruiker in Sleutelbeheer ook de woningen van de gekozen medewerkers.</div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-44 overflow-y-auto">
      {users.map((u) => (
        <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`key-also-${u.id}`}>
          <Checkbox checked={value.includes(u.id)} onCheckedChange={(v) => onChange(v ? [...value, u.id] : value.filter((x) => x !== u.id))} />
          <span className="truncate">{u.name} <span className="text-muted-foreground text-xs">· {ROLE_LABELS[u.role]}</span></span>
        </label>
      ))}
    </div>
  </div>
);

export default function Users() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [houses, setHouses] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [results, setResults] = useState(null);
  const [copied, setCopied] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", email: "" });
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState(emptyInvite);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState("");

  const load = () => api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    api.get("/houses").then(({ data }) => setHouses(data)).catch(() => setHouses([]));
  }, []);

  const setRole = (role) => setNewForm((f) => ({ ...f, role, house_ids: [], ...chatDefaults(role) }));
  const toggleHouse = (hid) => setNewForm((f) => ({ ...f, house_ids: f.house_ids.includes(hid) ? f.house_ids.filter((x) => x !== hid) : [...f.house_ids, hid] }));

  const invite = async () => {
    setCreating(true);
    try {
      const showHouses = HOUSE_ROLES.includes(newForm.role);
      const payload = {
        name: newForm.name, email: newForm.email.toLowerCase(), phone: newForm.phone, role: newForm.role,
        house_ids: showHouses ? newForm.house_ids : [],
        chat_house: newForm.role === "admin" ? true : !!newForm.chat_house,
        chat_guest: newForm.role === "admin" ? true : !!newForm.chat_guest,
        chat_direct: !!newForm.chat_direct,
        hourly_rate: STAFF_SUBROLES.includes(newForm.role) ? (newForm.hourly_rate === "" ? null : Number(newForm.hourly_rate)) : null,
        language: newForm.language || "nl",
      };
      const { data } = await api.post("/users/invite", payload);
      toast.success(data.emailed
        ? `Uitnodiging verzonden naar ${payload.email}`
        : `Uitgenodigd, maar de e-mail kon niet worden verzonden — controleer het adres`);
      setNewOpen(false);
      setNewForm(emptyInvite);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setCreating(false); }
  };

  const resendInvite = async (id, name) => {
    try {
      const { data } = await api.post(`/users/${id}/resend-invite`);
      toast.success(data.emailed ? `Uitnodiging opnieuw verzonden naar ${name}` : "Uitnodiging vernieuwd, maar e-mail kon niet worden verzonden");
    } catch (err) { toast.error(apiError(err)); }
  };

  const openEdit = (u) => {
    setEditForm({
      name: u.name || "", email: u.email || "", phone: u.phone || "",
      sees_all_own_type: !!u.sees_all_own_type, key_access: !!u.key_access, key_also_users: u.key_also_users || [], role: u.role,
      hourly_rate: u.hourly_rate != null ? String(u.hourly_rate) : "",
      language: u.language || "nl",
      ...chatDefaults(u.role),
      ...(u.chat_house != null ? { chat_house: !!u.chat_house } : {}),
      ...(u.chat_guest != null ? { chat_guest: !!u.chat_guest } : {}),
      ...(u.chat_direct != null ? { chat_direct: !!u.chat_direct } : {}),
    });
    setEditUser(u);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, {
        name: editForm.name,
        email: editForm.email.toLowerCase(),
        phone: editForm.phone,
        sees_all_own_type: editForm.sees_all_own_type,
        key_access: editForm.key_access,
        key_also_users: editForm.key_access ? editForm.key_also_users || [] : [],
        chat_house: editUser.role === "admin" ? true : !!editForm.chat_house,
        chat_guest: editUser.role === "admin" ? true : !!editForm.chat_guest,
        chat_direct: !!editForm.chat_direct,
        hourly_rate: STAFF_SUBROLES.includes(editUser.role) ? (editForm.hourly_rate === "" ? null : Number(editForm.hourly_rate)) : null,
        language: editForm.language || "nl",
      });
      toast.success("Gebruiker bijgewerkt");
      if (editUser.id === user.id) refresh();
      setEditUser(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const changeRole = async (id, role) => {
    try { await api.patch(`/users/${id}`, { role }); toast.success("Rol bijgewerkt"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Gebruiker verwijderen?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Verwijderd"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const syncOwners = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/owners/sync");
      setResults(data);
      toast.success(`${data.created} eigenaren aangemaakt, ${data.linked_existing} bestaande gekoppeld`);
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSyncing(false); }
  };

  const resetPwd = async (id, name, email) => {
    if (!window.confirm(`Nieuw tijdelijk wachtwoord aanmaken voor ${name}?`)) return;
    try {
      const { data } = await api.post(`/users/${id}/reset-password`);
      setResults({ created: 0, linked_existing: 1, results: [{ email, name, created: true, temp_password: data.temp_password }] });
    } catch (err) { toast.error(apiError(err)); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); setCopied(text); setTimeout(() => setCopied(null), 1500); };

  const canManage = ["admin"].includes(user.role);
  const showHouses = HOUSE_ROLES.includes(newForm.role);
  const isPending = (u) => u.active === false || u.status === "invited";

  const q = search.trim().toLowerCase();
  const filteredUsers = !users ? users : (q ? users.filter((u) => [u.name, u.email, ROLE_LABELS[u.role]].filter(Boolean).join(" ").toLowerCase().includes(q)) : users);

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Gebruikers</h1>
          <p className="text-muted-foreground mt-1">Uitnodigen, rollen en toegang beheren.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => { setNewForm(emptyInvite); setNewOpen(true); }} className="bg-primary hover:bg-primary/90 gap-2" data-testid="invite-user-btn">
              <UserPlus className="h-4 w-4" /> Gebruiker uitnodigen
            </Button>
            <Button onClick={syncOwners} disabled={syncing} variant="outline" className="gap-2" data-testid="sync-owners-btn">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Koppel eigenaren uit woningen
            </Button>
          </div>
        )}
      </div>

      {!users ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <>
          {users.length > 0 && (
            <div className="relative mb-4 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op naam, e-mail of rol…" className="pl-9" data-testid="users-search" />
            </div>
          )}
          <DeviceSummary users={users.filter((u) => !isPending(u))} />
          {filteredUsers.length === 0 ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground" data-testid="users-no-results">
              <Search className="h-10 w-10 mx-auto mb-3 opacity-40" /> Geen gebruikers gevonden voor "{search}".
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
              {filteredUsers.map((u) => (
            <div key={u.id} className="flex items-center gap-4 p-4 border-b border-border last:border-0 flex-wrap" data-testid={`user-row-${u.id}`}>
              <span className="grid place-items-center h-10 w-10 rounded-full bg-secondary/15 text-secondary font-display font-semibold shrink-0">{u.name?.[0]?.toUpperCase()}</span>
              <div className="flex-1 min-w-[160px]">
                <div className="font-semibold flex items-center gap-2">
                  {u.name} {u.id === user.id && <Badge variant="outline" className="text-xs">jij</Badge>}
                  {isPending(u) && <Badge className="bg-amber-100 text-amber-800 gap-1 text-xs" data-testid={`pending-badge-${u.id}`}><Clock className="h-3 w-3" /> Uitnodiging verzonden</Badge>}
                </div>
                <div className="text-sm text-muted-foreground">{u.email}</div>
                {!isPending(u) && <DeviceStatus u={u} />}
              </div>
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user.id || user.role !== "admin"}>
                <SelectTrigger className="w-44" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
              {canManage && (
                <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="gap-1.5" data-testid={`edit-user-${u.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Bewerk
                </Button>
              )}
              {u.id !== user.id && !isPending(u) && (
                <Button size="sm" variant="outline" onClick={() => navigate(`/app/chat?user=${u.id}`)} className="gap-1.5" data-testid={`chat-user-${u.id}`}>
                  <MessageCircle className="h-4 w-4" /> Chat
                </Button>
              )}
              {u.id !== user.id && canManage && isPending(u) && (
                <Button size="sm" variant="outline" onClick={() => resendInvite(u.id, u.name)} className="gap-1.5" data-testid={`resend-invite-${u.id}`}>
                  <RefreshCw className="h-3.5 w-3.5" /> Opnieuw uitnodigen
                </Button>
              )}
              {u.id !== user.id && canManage && !isPending(u) && (
                <Button size="sm" variant="outline" onClick={() => resetPwd(u.id, u.name, u.email)} className="gap-1.5" data-testid={`reset-pwd-${u.id}`}>
                  <KeyRound className="h-3.5 w-3.5" /> Wachtwoord
                </Button>
              )}
              {u.id !== user.id && user.role === "admin" && (
                <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
              )}
            </div>
          ))}
            </div>
          )}
        </>
      )}

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="edit-user-dialog">
          <DialogHeader><DialogTitle className="font-display">Gebruiker bewerken</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-name">Naam</Label>
              <Input id="edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" data-testid="edit-user-name" />
            </div>
            <div>
              <Label htmlFor="edit-email">E-mailadres</Label>
              <Input id="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1.5" data-testid="edit-user-email" />
              <p className="text-xs text-muted-foreground mt-1.5">Notificaties en herstel-mails gaan naar dit adres.</p>
            </div>
            <div>
              <Label htmlFor="edit-phone">Mobiel (incl. landcode)</Label>
              <Input id="edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="mt-1.5" placeholder="+31 6 12345678" data-testid="edit-user-phone" />
              <p className="text-xs text-muted-foreground mt-1.5">Voor WhatsApp-berichten (bijv. taak-toewijzingen). Gebruik landcode, bijv. +31.</p>
            </div>
            <LanguageSelect value={editForm.language} onChange={(v) => setEditForm({ ...editForm, language: v })} testId="edit-user-language" />
            {editUser && STAFF_SUBROLES.includes(editUser.role) && (
              <div>
                <Label htmlFor="edit-rate">Uurtarief (€)</Label>
                <Input id="edit-rate" type="number" min="0" step="0.01" value={editForm.hourly_rate} onChange={(e) => setEditForm({ ...editForm, hourly_rate: e.target.value })} className="mt-1.5" placeholder="bijv. 25" data-testid="edit-hourly-rate" />
                <p className="text-xs text-muted-foreground mt-1.5">Gebruikt voor loonkosten in Kosten &amp; Facturatie (gewerkte uren × uurtarief).</p>
              </div>
            )}
            {editUser && editUser.role !== "admin" && (
              <ChatPermToggles form={editForm} setForm={setEditForm} role={editUser.role} />
            )}
            {editUser && ["cleaner", "subcontractor"].includes(editUser.role) && (
              <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer" data-testid="sees-all-toggle">
                <Checkbox checked={!!editForm.sees_all_own_type} onCheckedChange={(v) => setEditForm({ ...editForm, sees_all_own_type: !!v })} className="mt-0.5" data-testid="sees-all-checkbox" />
                <span className="text-sm">
                  <span className="font-medium">Ziet álle afspraken van zijn/haar type</span>
                  <span className="block text-xs text-muted-foreground">Bijv. een hoofd-schoonmaker die alle schoonmaakafspraken mag zien, niet alleen de toegewezen.</span>
                </span>
              </label>
            )}
            {editUser && editUser.role !== "admin" && (
              <label className="flex items-start gap-3 rounded-lg border border-border p-3 cursor-pointer" data-testid="key-access-toggle">
                <Checkbox checked={!!editForm.key_access} onCheckedChange={(v) => setEditForm({ ...editForm, key_access: !!v })} className="mt-0.5" data-testid="key-access-checkbox" />
                <span className="text-sm">
                  <span className="font-medium">Toegang tot Sleutelbeheer</span>
                  <span className="block text-xs text-muted-foreground">Mag het sleutelbeheer-overzicht zien en bewerken. Admins hebben dit altijd.</span>
                </span>
              </label>
            )}
            {editUser && editUser.role !== "admin" && editForm.key_access && (
              <KeyAlsoUsers users={(users || []).filter((x) => x.id !== editUser.id && !["admin", "client", "guest"].includes(x.role))}
                value={editForm.key_also_users || []} onChange={(v) => setEditForm({ ...editForm, key_also_users: v })} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} data-testid="edit-user-cancel">Annuleer</Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-primary hover:bg-primary/90 gap-2" data-testid="edit-user-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="invite-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Gebruiker uitnodigen</DialogTitle>
            <DialogDescription>De gebruiker ontvangt een e-mail met een activatielink om zelf een wachtwoord in te stellen.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="new-name">Naam</Label>
              <Input id="new-name" value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} className="mt-1.5" data-testid="invite-name" />
            </div>
            <div>
              <Label htmlFor="new-email">E-mailadres</Label>
              <Input id="new-email" type="email" value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} className="mt-1.5" data-testid="invite-email" />
            </div>
            <div>
              <Label htmlFor="new-phone">Mobiel (incl. landcode)</Label>
              <Input id="new-phone" value={newForm.phone} onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })} className="mt-1.5" placeholder="+31 6 12345678" data-testid="invite-phone" />
              <p className="text-xs text-muted-foreground mt-1.5">WhatsApp-uitnodigingen volgen zodra Twilio uit sandbox is; nu wordt er per e-mail uitgenodigd.</p>
            </div>
            <LanguageSelect value={newForm.language} onChange={(v) => setNewForm({ ...newForm, language: v })} testId="invite-language" />
            <div>
              <Label>Rol</Label>
              <Select value={newForm.role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5" data-testid="invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {showHouses && (
              <div>
                <Label>Gekoppelde woningen</Label>
                <div className="mt-1.5 max-h-44 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="invite-houses">
                  {houses.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-3">Geen woningen beschikbaar.</p>
                  ) : houses.map((h) => (
                    <label key={h.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-accent/40" data-testid={`invite-house-${h.id}`}>
                      <Checkbox checked={newForm.house_ids.includes(h.id)} onCheckedChange={() => toggleHouse(h.id)} />
                      <span className="text-sm">{h.name}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">{newForm.house_ids.length} geselecteerd. Bepaalt óók welke woning- en gastchats zichtbaar zijn.</p>
              </div>
            )}
            {STAFF_SUBROLES.includes(newForm.role) && (
              <div>
                <Label htmlFor="new-rate">Uurtarief (€)</Label>
                <Input id="new-rate" type="number" min="0" step="0.01" value={newForm.hourly_rate} onChange={(e) => setNewForm({ ...newForm, hourly_rate: e.target.value })} className="mt-1.5" placeholder="bijv. 25" data-testid="invite-hourly-rate" />
                <p className="text-xs text-muted-foreground mt-1.5">Gebruikt voor loonkosten in Kosten &amp; Facturatie (gewerkte uren × uurtarief).</p>
              </div>
            )}
            {newForm.role !== "admin" && (
              <ChatPermToggles form={newForm} setForm={setNewForm} role={newForm.role} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Annuleer</Button>
            <Button onClick={invite} disabled={creating || !newForm.name || !newForm.email} className="bg-primary hover:bg-primary/90 gap-2" data-testid="invite-send-btn">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Uitnodiging versturen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!results} onOpenChange={(o) => !o && setResults(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="sync-results-dialog">
          <DialogHeader><DialogTitle className="font-display">Tijdelijke wachtwoorden</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Nieuwe gebruikers ontvangen deze inloggegevens automatisch per e-mail (mits het e-mailadres klopt). Je kunt ze desgewenst ook handmatig delen. Ze worden maar één keer getoond.</p>
          <div className="space-y-2 mt-2">
            {results?.results?.filter((r) => r.temp_password).length === 0 && (
              <p className="text-sm text-muted-foreground">Geen nieuwe wachtwoorden — alle eigenaren bestonden al en zijn gekoppeld.</p>
            )}
            {results?.results?.filter((r) => r.temp_password).map((r) => (
              <div key={r.email} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-accent/40">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <code className="text-sm bg-background border border-border rounded px-2 py-1">{r.temp_password}</code>
                  <Button size="icon" variant="ghost" onClick={() => copy(r.temp_password)} className="h-8 w-8">
                    {copied === r.temp_password ? <Check className="h-4 w-4 text-secondary" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
